import ErrorAlert from "@/utility/ErrorAlert";
import http from "@/axios/http-common";
import { PpiRekapCakupan as UnitPpiRekapCakupan } from "@/utils/unit";

const BASE = "/ppi/bundle-rekap-bulanan";

function handle<T>(p: Promise<T>): Promise<T> {
  return p.catch(err => {
    ErrorAlert.showErrorAlert(err);
    return Promise.reject(err);
  });
}

export type PpiRekapMode =
  | "harian"
  | "bulanan"
  | "triwulan"
  | "semester"
  | "tahunan";

export type PpiTrenMode = Exclude<PpiRekapMode, "harian">;

export type PpiRekapCakupan = UnitPpiRekapCakupan;

export interface PpiRekapFase {
  n: number;
  num: number;
  den: number;
  capaian: number | null;
  kategori: "baik" | "intermediate" | "minimal" | "-";
}

export interface PpiRekapPhaseMeta {
  key: string;
  bundle: string;
  bundle_label: string;
  fase_label: string;
  jumlah_item: number;
}

export interface PpiRekapRuangan {
  unit_id: string;
  unit_nama: string;
  fase: Record<string, PpiRekapFase>;
}

export interface PpiRekapMatriks {
  mode: PpiRekapMode;
  periode: string;
  periode_label: string;
  periode_prefix: string;
  cakupan?: PpiRekapCakupan;
  cakupan_label?: string;
  phases: PpiRekapPhaseMeta[];
  ruangan: PpiRekapRuangan[];
  jumlah: Record<string, PpiRekapFase>;
  dihitung_pada: string;
}

export interface PpiRekapAnalisaRow {
  fase_key: string;
  fase_label: string;
  capaian: number | null;
  kategori: string;
  analisa: string | null;
  rtl: string | null;
}

export interface PpiRekapLaporan {
  id: number;
  periode: string;
  periode_label: string;
  analisa: PpiRekapAnalisaRow[] | null;
  catatan: string | null;
  status: string;
  is_locked: boolean;
  status_label: { label: string; color: string };
  signed_ketua_ppi_at: string | null;
  signed_ketua_ppi?: any;
}

export interface PpiUnitScope {
  is_kepala_ruang: boolean;
  unit_id: string | null;
  unit_nama: string | null;
}

export interface PpiRekapPreview {
  mode: PpiRekapMode;
  periode: string;
  periode_label: string;
  periode_prefix: string;
  matriks: PpiRekapMatriks;
  laporan: PpiRekapLaporan | null;
  unit_scope: PpiUnitScope;
}

export interface PpiBangsalItem {
  field: string;
  label: string;
  num: number;
  den: number;
  capaian: number | null;
  kategori: "baik" | "intermediate" | "minimal" | "-";
}

export interface PpiBangsalFase extends PpiRekapFase {
  key: string;
  label: string;
  items: PpiBangsalItem[];
}

export interface PpiBangsalBundle {
  key: string;
  label: string;
  fase: PpiBangsalFase[];
}

export interface PpiBangsalLaporan {
  mode: PpiRekapMode;
  periode: string;
  periode_label: string;
  periode_prefix: string;
  unit_id: string;
  unit_nama: string;
  bundles: PpiBangsalBundle[];
  dihitung_pada: string;
}

export type PpiStatusPengisian = "belum" | "sebagian" | "lengkap";

export interface PpiStatusSel {
  n: number;
  terakhir: string | null;
}

export interface PpiStatusRuangan {
  unit_id: string;
  unit_nama: string;
  sel: Record<string, PpiStatusSel>;
  total: number;
  kategori_terisi: number;
  kategori_total: number;
  status: PpiStatusPengisian;
}

export interface PpiStatusMonitor {
  mode: PpiRekapMode;
  periode: string;
  periode_label: string;
  periode_prefix: string;
  kolom: Array<{ key: string; label: string }>;
  ruangan: PpiStatusRuangan[];
  ringkasan: {
    total_ruangan: number;
    belum: number;
    sebagian: number;
    lengkap: number;
  };
  dihitung_pada: string;
  unit_scope: PpiUnitScope;
}

export interface PpiTrenTitik {
  periode: string;
  label: string;
  singkat: string;
  dibekukan: boolean;
  fase: Record<string, PpiRekapFase>;
  bundle: Record<string, PpiRekapFase>;
}

export interface PpiBundleTren {
  tren: PpiTrenMode;
  tahun: number;
  rentang_label: string;
  cakupan: PpiRekapCakupan | null;
  cakupan_label: string | null;
  unit_id: string | null;
  unit_nama: string | null;
  phases: Array<Omit<PpiRekapPhaseMeta, "jumlah_item">>;
  bundles: Array<{ key: string; label: string }>;
  titik: PpiTrenTitik[];
  dihitung_pada: string;
}

const getList = (params?: { tahun?: string }) =>
  handle(http.getDataParam(BASE, { params }));

const preview = (
  periode: string,
  mode: PpiRekapMode = "bulanan",
  cakupan: PpiRekapCakupan = "ranap"
) =>
  handle(
    http.getDataParam(`${BASE}/preview`, { params: { periode, mode, cakupan } })
  );

const statusPengisian = (
  periode: string,
  mode: PpiRekapMode = "bulanan",
  cakupan: PpiRekapCakupan = "ranap"
) =>
  handle(
    http.getDataParam(`${BASE}/status-pengisian`, {
      params: { periode, mode, cakupan }
    })
  );

const tren = (
  trenMode: PpiTrenMode,
  tahun: string,
  cakupan: PpiRekapCakupan = "ranap",
  unitId?: string | null
) =>
  handle(
    http.getDataParam(`${BASE}/tren`, {
      params: { tren: trenMode, tahun, cakupan, unit_id: unitId || undefined }
    })
  );

const find = (id: number) => handle(http.getData(`${BASE}/${id}`));

const save = (data: {
  periode: string;
  catatan?: string | null;
  analisa?: Array<{
    fase_key: string;
    analisa?: string | null;
    rtl?: string | null;
  }>;
}) => handle(http.postData(BASE, data));

const destroy = (id: number) => handle(http.deleteData(`${BASE}/${id}`));

const signKetuaPpi = (id: number, data: { password: string }) =>
  handle(http.postData(`${BASE}/${id}/sign-ketua-ppi`, data));

const printPdf = (id: number) => {
  window.open(`${http.getAPI()}${BASE}/${id}/pdf`, "_blank");
};

const perBangsal = (
  periode: string,
  unitId?: string | null,
  mode: PpiRekapMode = "bulanan"
) =>
  handle(
    http.getDataParam(`${BASE}/per-bangsal`, {
      params: { periode, mode, unit_id: unitId || undefined }
    })
  );

const bukaPdfBlob = (resp: any) => {
  const url = URL.createObjectURL(
    new Blob([resp.data], { type: "application/pdf" })
  );
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const printPerBangsalPdf = async (
  periode: string,
  unitId?: string | null,
  mode: PpiRekapMode = "bulanan"
) => {
  const resp: any = await handle(
    http.getDataParam(`${BASE}/per-bangsal/pdf`, {
      params: { periode, mode, unit_id: unitId || undefined },
      responseType: "blob"
    })
  );
  bukaPdfBlob(resp);
};

const printMatriksPdf = async (
  periode: string,
  mode: PpiRekapMode = "bulanan",
  cakupan: PpiRekapCakupan = "ranap"
) => {
  const resp: any = await handle(
    http.getDataParam(`${BASE}/matriks/pdf`, {
      params: { periode, mode, cakupan },
      responseType: "blob"
    })
  );
  bukaPdfBlob(resp);
};

export default {
  getList,
  preview,
  perBangsal,
  printPerBangsalPdf,
  printMatriksPdf,
  statusPengisian,
  tren,
  find,
  save,
  destroy,
  signKetuaPpi,
  printPdf
};

